import { config } from 'src/config';
import { configureRetry } from 'src/shared/utils/retry';

import { isMobileApp } from './environment';

const networkErrorMap: { [key: string]: boolean } = {
  'Load failed': true, // safari
  'Failed to fetch': true, // chrome
};

class StatusError extends Error {
  public response: Response;

  constructor(response: Response) {
    super(`${response.status}: ${response.statusText}`);
    this.response = response;
  }
}

/**
 * A lightweight fetch wrapper
 *
 * @param {RequestInfo} resource
 * @param {RequestInit} [init]
 * @returns {Promise<Response>}
 * @throws {TypeError | AbortError | StatusError}
 */
const request = async (
  resource: RequestInfo,
  init?: RequestInit,
): Promise<Response> => {
  let response: Response | undefined;

  try {
    // eslint-disable-next-line no-restricted-globals
    response = await fetch(resource, {
      ...init,
      headers: {
        'x-client-id': isMobileApp() ? 'mobile-webview-web-react' : 'web-react',
        'x-client-version': config.APP_VERSION,
        ...init?.headers,
      },
    });
  } catch (e) {
    // Normalize network error message
    if (e instanceof TypeError && networkErrorMap[e.message]) {
      if (networkErrorMap[e.message]) {
        e.message = 'Failed to fetch';
      }
    }
    throw e;
  }

  if (!response.ok) {
    throw new StatusError(response);
  }

  return response;
};

const requestWithRetry = async (resource: RequestInfo, init?: RequestInit) => {
  const { withRetry } = configureRetry();
  return withRetry(() => request(resource, init))();
};

export { StatusError, request, requestWithRetry };
